import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  List,
  Avatar,
  Tag,
  message,
  Form,
  Input,
  Popconfirm,
  Empty,
} from 'antd';
import {
  ArrowRightOutlined,
  MessageOutlined,
  SendOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { messagesApi } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const ViewMessage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [chatDetails, setChatDetails] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleBack = useCallback(() => {
    navigate(returnToRef.current || '/messages', { state: buildGoBackState() });
  }, [navigate, returnToRef, buildGoBackState]);

  const fetchChat = async () => {
    setLoading(true);
    try {
      const res = await messagesApi.getByUserId(id);
      setChatDetails(res?.chat_details || []);
      setUnreadCount(Number(res?.unread_message || 0));
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت گفتگو');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChat();
  }, [id]);

  const peerName = useMemo(() => {
    const first = chatDetails?.[0];
    if (!first) return `گفتگو #${id}`;
    return first.to_user?.name || first.from_user?.name || `گفتگو #${id}`;
  }, [chatDetails, id]);

  const submitMessage = async (values) => {
    setSubmitting(true);
    try {
      await messagesApi.create({
        user_type: 'employee',
        user_id: Number(id),
        client_id: null,
        message: values.message,
        types: 'chat',
      });
      form.resetFields();
      message.success('ارسال شد');
      fetchChat();
    } catch (e) {
      message.error(e?.message || 'خطا در ارسال پیام');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMsg = async (msgId) => {
    try {
      await messagesApi.delete(msgId);
      message.success('پیام حذف شد');
      fetchChat();
    } catch (e) {
      message.error(e?.message || 'خطا در حذف پیام');
    }
  };

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 flex items-center justify-center">
                <MessageOutlined className="text-cyan-600 dark:text-cyan-400 text-xl" />
              </div>
              <Space className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 truncate">{peerName}</h1>
                {unreadCount > 0 ? <Tag color="red">{unreadCount} خوانده‌نشده</Tag> : null}
              </Space>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={handleBack}>
              بازگشت
            </Button>
          </div>

          <div className="p-3 sm:p-6 bg-gray-50 dark:bg-gray-900/50 space-y-4">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <List
                loading={loading}
                dataSource={chatDetails}
                locale={{ emptyText: <Empty description="پیامی وجود ندارد" /> }}
                renderItem={(item) => {
                  const sender = item.from_user || {};
                  const isSeen = item.message_seen === 'yes';
                  return (
                    <List.Item
                      key={item.id}
                      className="!items-start"
                      actions={[
                        <Popconfirm
                          key="del"
                          title="حذف این پیام؟"
                          okText="حذف"
                          cancelText="لغو"
                          onConfirm={() => deleteMsg(item.id)}
                        >
                          <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar src={getImagePath(sender.image_url)}>
                            {(sender.name || '?').charAt(0)}
                          </Avatar>
                        }
                        title={
                          <Space>
                            <span>{sender.name || '—'}</span>
                            <Tag color={isSeen ? 'green' : 'orange'}>
                              {isSeen ? 'خوانده شده' : 'خوانده نشده'}
                            </Tag>
                          </Space>
                        }
                        description={<div className="whitespace-pre-wrap break-words">{item.message || '—'}</div>}
                      />
                    </List.Item>
                  );
                }}
              />
            </Card>

            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Form form={form} layout="vertical" onFinish={submitMessage}>
                <Form.Item name="message" label="ارسال پیام جدید" rules={[{ required: true }]}>
                  <Input.TextArea rows={4} />
                </Form.Item>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  htmlType="submit"
                  loading={submitting}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  ارسال
                </Button>
              </Form>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewMessage;
